import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, FileTextIcon, CheckIcon, FolderIcon } from 'lucide-react';
import { Condition, ConditionGroup } from '../types';
interface DocumentAssignmentModalProps {
  isOpen: boolean;
  conditions: Condition[];
  groups: ConditionGroup[];
  onClose: () => void;
  onSubmit: (documentName: string, conditionIds: string[]) => void;
}
export function DocumentAssignmentModal({
  isOpen,
  conditions,
  groups,
  onClose,
  onSubmit
}: DocumentAssignmentModalProps) {
  const [documentName, setDocumentName] = useState('');
  const [selectedConditions, setSelectedConditions] = useState<Set<string>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isOpen) {
      setDocumentName('');
      setSelectedConditions(new Set());
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);
  const toggleCondition = (conditionId: string) => {
    setSelectedConditions(prev => {
      const next = new Set(prev);
      if (next.has(conditionId)) {
        next.delete(conditionId);
      } else {
        next.add(conditionId);
      }
      return next;
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (documentName.trim() && selectedConditions.size > 0) {
      onSubmit(documentName.trim(), Array.from(selectedConditions));
      onClose();
    }
  };
  const unclearedConditions = conditions.filter(c => c.status !== 'cleared');
  // Group conditions for display
  const groupedConditions = groups.map(g => ({
    group: g,
    conditions: unclearedConditions.filter(c => c.groupId === g.id)
  })).filter(g => g.conditions.length > 0);
  const ungroupedConditions = unclearedConditions.filter(c => !c.groupId);
  return <AnimatePresence data-id="element-192">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-193">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} data-id="element-194" />

          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden" data-id="element-195">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100" data-id="element-196">
              <div className="flex items-center gap-3" data-id="element-197">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center" data-id="element-198">
                  <FileTextIcon className="w-5 h-5 text-blue-600" data-id="element-199" />
                </div>
                <div data-id="element-200">
                  <h2 className="font-semibold text-slate-900" data-id="element-201">
                    Upload Document
                  </h2>
                  <p className="text-sm text-slate-500" data-id="element-202">
                    Select conditions this document satisfies
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 text-slate-400 hover:bg-slate-100 rounded-lg" data-id="element-203">
                <XIcon className="w-5 h-5" data-id="element-204" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden" data-id="element-205">
              <div className="px-6 py-4 border-b border-slate-100" data-id="element-206">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-207">
                  Document Name
                </label>
                <input ref={inputRef} type="text" value={documentName} onChange={e => setDocumentName(e.target.value)} placeholder="e.g., Bank Statement.pdf" className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none" data-id="element-208" />
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-4" data-id="element-209">
                {unclearedConditions.length === 0 ? <div className="text-center py-8 text-slate-500 text-sm" data-id="element-210">
                    All conditions are already cleared!
                  </div> : <div className="space-y-6" data-id="element-211">
                    {/* Select All / Deselect All */}
                    <div className="flex items-center justify-between pb-3 border-b border-slate-100" data-id="element-212">
                      <span className="text-sm text-slate-600" data-id="element-213">
                        {unclearedConditions.length} outstanding condition
                        {unclearedConditions.length !== 1 ? 's' : ''}
                      </span>
                      <button type="button" onClick={() => {
                  if (selectedConditions.size === unclearedConditions.length) {
                    setSelectedConditions(new Set());
                  } else {
                    setSelectedConditions(new Set(unclearedConditions.map(c => c.id)));
                  }
                }} className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors" data-id="element-214">
                        {selectedConditions.size === unclearedConditions.length ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                    {ungroupedConditions.length > 0 && <div data-id="element-215">
                        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-216">
                          Ungrouped Conditions
                        </h4>
                        <div className="space-y-1" data-id="element-217">
                          {ungroupedConditions.map(c => <ConditionCheckbox key={c.id} condition={c} selected={selectedConditions.has(c.id)} onToggle={() => toggleCondition(c.id)} data-id="element-218" />)}
                        </div>
                      </div>}

                    {groupedConditions.map(({
                group,
                conditions
              }) => <div key={group.id} data-id="element-219">
                        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5" data-id="element-220">
                          <FolderIcon className="w-3.5 h-3.5" data-id="element-221" />
                          {group.name}
                        </h4>
                        <div className="space-y-1 ml-2 border-l-2 border-slate-100 pl-2" data-id="element-222">
                          {conditions.map(c => <ConditionCheckbox key={c.id} condition={c} selected={selectedConditions.has(c.id)} onToggle={() => toggleCondition(c.id)} data-id="element-223" />)}
                        </div>
                      </div>)}
                  </div>}
              </div>

              <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between" data-id="element-224">
                <span className="text-sm text-slate-600 font-medium" data-id="element-225">
                  {selectedConditions.size} selected
                </span>
                <div className="flex gap-2" data-id="element-226">
                  <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 rounded-md" data-id="element-227">
                    Cancel
                  </button>
                  <button type="submit" disabled={!documentName.trim() || selectedConditions.size === 0} className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-228">
                    Upload & Clear
                  </button>
                </div>
              </div>
            </form>
          </motion.div>
        </div>}
    </AnimatePresence>;
}
function ConditionCheckbox({
  condition,
  selected,
  onToggle
}: {
  condition: Condition;
  selected: boolean;
  onToggle: () => void;
}) {
  return <label className="flex items-start gap-3 py-2 px-2 rounded hover:bg-slate-50 cursor-pointer group" data-id="element-229">
      <div className="relative mt-0.5" data-id="element-230">
        <input type="checkbox" checked={selected} onChange={onToggle} className="sr-only" data-id="element-231" />
        <div className={`w-4 h-4 rounded border flex items-center justify-center ${selected ? 'bg-blue-600 border-blue-600' : 'border-slate-300 group-hover:border-blue-400'}`} data-id="element-232">
          {selected && <CheckIcon className="w-3 h-3 text-white" data-id="element-233" />}
        </div>
      </div>
      <div data-id="element-234">
        <div className="text-sm font-medium text-slate-800" data-id="element-235">
          {condition.name}
        </div>
        {condition.description && <div className="text-xs text-slate-500 line-clamp-1" data-id="element-236">
            {condition.description}
          </div>}
      </div>
    </label>;
}